import { t } from 'i18next';
import { isNil } from 'lodash';
import { Controller, Form, FormProvider } from 'react-hook-form';
import {
  Box,
  Radio,
  Button,
  Typography,
  RadioGroup,
  FormControl,
  FormHelperText,
  FormControlLabel,
  ButtonProps,
  Autocomplete,
  TextField,
  FormGroup,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {
  DatepickerFormField,
  NumberInputFormField,
  PatientsSearchFormField,
  ProviderSearchFormField,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
} from 'components/DynamicFormFields';

import { CustomModal } from '../CustomModal';

import { useAddTask } from './hooks';
import { theme } from '@store';
import { SaveChangesModal } from '../SaveChangesModal';
import { SearchField } from '../SearchField';
import {
  MAXIMUM_STRING_LENGTH,
  MAXIMUM_TEXT_AREA_LENGTH,
} from '@common/types/constants.ts';
import { Select } from '@ui';
import { StatusBanner } from 'components/StatusBaner';

export const AddTask = ({
  variant = 'contained',
  color = 'secondary',
  openOnTaskCreate = true,
  // fallback query for populating the patient search field options
  searchQuery = '',
  buttonText = 'ADD TASK',
  ...props
}: ButtonProps & {
  openOnTaskCreate?: boolean;
  searchQuery?: string;
  buttonText?: string;
}) => {
  const {
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    assignOptions,
    teamOptions,
    userOptions,
    recurrenceOptions,
    formMethods,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isLoading,
    setTeamsSearchQuery,
    teamsSearchQuery,
    userSearchQuery,
    setUserSearchQuery,
    urgencyOptions,
    orderTypeOptions,
    orderSetOptions,
    orderTypeCategory,
    taskType,
    orderType,
    orderSet,
    assignTo,
    selectedRecurrence,
    labRequirementsOptions,
    homeHealthAgencyNameOptions,
    homeHealthProviderStatusOptions,
    hospiceReferralOriginOptions,
    externalHospiceReferralReasonOptions,
    programOptions,
    homeHealthAgencyName,
    externalHospiceAgencyName,
    externalHospiceReferralReason,
    hospiceReferralOrigin,
    reasonForExternalReferralOptions,
    reasonForExternalReferral,
    noteTypeOptions,
    careManagerId,
    userId,
    patientAdditionalInformation,
    disabled,
  } = useAddTask(searchQuery, openOnTaskCreate);

  const { control, getValues, setValue } = formMethods;

  const bannerText =
    'It is recommended to add this task directly in Brigade EMR. Doing so will automatically sync it to Bridge and create the corresponding task there. If you choose to create it in Bridge instead, you will need to review the Care Coordination care note in Brigade EMR to ensure the details are correct and sign it.';

  const medicationBannerOrderSets = [
    'New Prescription',
    'Refill',
    'Discharge',
    'Medication dosage / frequency change',
    'Medication Statement',
    'New Med Order',
    'Change Med Order',
  ];

  const outsideSpecialistOrderSets = [
    'Cardiologist',
    'Cardiothoracic surgeon',
    'Dental',
    'ENT',
    'Endocrinologist',
    'Gastroenterologist',
    'General surgeon',
    'Infectious disease specialist',
    'Neurologist',
    'Neurosurgeon',
    'OBGYN',
    'Ophthalmologist',
    'Orthopedic surgeon',
    'Pain Management',
    'Podiatrist',
    'Private Duty Home Care',
    'Pulmonologist',
    'Rheumatologist',
    'Sleep Study',
    'Social Worker',
    'Urologist',
    'Vascular Surgeon',
    'Other',
  ];

  const palliativeCareOrderSets = ['Internal Referral', 'External Referral'];

  const psychOrderSets = ['Consult', 'Other'];

  const neurologyOrderSets = [
    'Neurologist-provider consult',
    'Neurologist-patient consult',
  ];

  const isBannerShown =
    (!!patientAdditionalInformation?.canvas_id &&
      (orderType === 'Labs' ||
        orderType === 'Imaging' ||
        orderType === 'Hospice' ||
        orderType === 'Home Health')) ||
    (orderType === 'Medication' &&
      medicationBannerOrderSets.includes(orderSet ?? '')) ||
    (orderType === 'Outside Specialist' &&
      outsideSpecialistOrderSets.includes(orderSet ?? '')) ||
    (orderType === 'Palliative Care' &&
      palliativeCareOrderSets.includes(orderSet ?? '')) ||
    (orderType === 'Psych' && psychOrderSets.includes(orderSet ?? '')) ||
    (orderType === 'Neurology' && neurologyOrderSets.includes(orderSet ?? ''));

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        sx={{ fontWeight: 'regular', fontSize: '1rem', color: 'white' }}
        color={color}
        variant={variant}
        onClick={handleOnClick}
        {...props}
        disabled={disabled || props.disabled}
      >
        {buttonText}
      </Button>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('worklist.addCustomTask.title')}
        isLoading={isSubmitting}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <SelectFormField
              name='category'
              label={t('worklist.addCustomTask.category')}
              options={[
                {
                  value: 'Care management',
                  label: t('worklist.addCustomTask.careManagementType'),
                },
                {
                  value: 'Custom',
                  label: t('worklist.addCustomTask.customType'),
                },
                {
                  value: 'Pharmacy',
                  label: t('worklist.addCustomTask.pharmacyType'),
                },
              ]}
            />
            {taskType !== 'Care management' && (
              <TextFormField
                name='title'
                label={t('worklist.addCustomTask.name')}
                optional={false}
                disabled={getValues('category') !== 'Custom'}
              />
            )}
            <TextAreaFormField
              name='description'
              disabled={getValues('category') === 'Pharmacy'}
              label={t('worklist.addCustomTask.description')}
              charLimit={MAXIMUM_TEXT_AREA_LENGTH}
              optional={taskType !== 'Care management'}
            />
            {taskType === 'Care management' && (
              <Controller
                name='urgency'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        disablePortal
                        options={urgencyOptions}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        getOptionLabel={opt => opt.name}
                        value={
                          urgencyOptions.find(o => o.id === field.value) ?? null
                        }
                        onChange={(_, v) => field.onChange(v?.id)}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t('worklist.addCustomTask.urgency.label')}
                            error={!isNil(error)}
                            required
                          />
                        )}
                      />
                      <FormHelperText error={!isNil(error)}>
                        {error ? error.message : null}
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              />
            )}
            <DatepickerFormField
              name='due_date'
              label={`${t('worklist.addCustomTask.dueDate')} ${
                taskType === 'Care management' ? ' (optional)' : ''
              }`}
              disableFuture={false}
              disablePast={true}
              disabled={
                !['Care management', 'Custom'].includes(getValues('category'))
              }
              optional={taskType === 'Care management'}
              helperText='Due date overrides urgency dates if filled in'
            />
            <PatientsSearchFormField
              name='patient_id'
              placeholder={t('worklist.addCustomTask.patient')}
              searchQuery={searchQuery}
            />
            {taskType === 'Care management' && (
              <ProviderSearchFormField
                name='provider_id'
                placeholder={t('worklist.addCustomTask.provider')}
              />
            )}
            <Box>
              <Typography variant='body1' component='h2'>
                {t('worklist.addCustomTask.typeRadioGroupDescription')} *:
              </Typography>
              <Controller
                name={'assign_to'}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl>
                    <RadioGroup
                      sx={{
                        ml: '1rem',
                      }}
                      row
                      {...field}
                      defaultValue={assignOptions[0]}
                      onChange={(_e, val) => {
                        field.onChange(val);
                        const autoSelectedValue =
                          val === 'self'
                            ? userId
                            : val === 'assignedCm'
                              ? careManagerId
                              : '';
                        setUserSearchQuery('');
                        setValue('user_id', autoSelectedValue, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    >
                      {assignOptions.map(option => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio disabled={option.disabled} />}
                          label={option.label}
                        />
                      ))}
                    </RadioGroup>
                    <FormHelperText error={!isNil(error)}>
                      {error ? error.message : null}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Box>
            {assignTo !== 'team' && (
              <Controller
                control={control}
                name='user_id'
                render={({ field, fieldState: { error } }) => (
                  <FormControl>
                    <SearchField
                      name={'user_id'}
                      search={userSearchQuery}
                      onSearchChange={setUserSearchQuery}
                      value={field.value || ''}
                      options={userOptions}
                      onValueChange={value => field.onChange(value)}
                      disabled={
                        assignTo === 'self' || assignTo === 'assignedCm'
                      }
                      placeholder={t('worklist.addCustomTask.assignee')}
                      error={!isNil(error)}
                    />
                    <FormHelperText error={!isNil(error)}>
                      {error ? error.message : null}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            )}
            {assignTo === 'team' && (
              <Controller
                control={control}
                name='team_id'
                render={({ field, fieldState: { error } }) => (
                  <FormControl>
                    <SearchField
                      name={'team_id'}
                      search={teamsSearchQuery}
                      onSearchChange={setTeamsSearchQuery}
                      value={field.value || ''}
                      options={teamOptions}
                      onValueChange={value => field.onChange(value)}
                      placeholder={t('worklist.addCustomTask.teamSelectLabel')}
                      error={!isNil(error)}
                    />
                    <FormHelperText error={!isNil(error)}>
                      {error ? error.message : null}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            )}

            {isBannerShown && (
              <StatusBanner text={bannerText} variant={'info'} />
            )}

            {taskType === 'Care management' && (
              <Controller
                name='order_type'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        {...field}
                        disablePortal
                        options={orderTypeOptions}
                        getOptionLabel={(option: {
                          id: string;
                          name: string;
                        }) => option.name}
                        onChange={(event, newValue) => {
                          field.onChange(newValue?.id);
                        }}
                        value={orderTypeOptions.find(
                          (item: { id: string; name: string }) =>
                            item.id === field.value,
                        )}
                        renderInput={params => {
                          return (
                            <TextField
                              {...params}
                              error={!isNil(error)}
                              required
                              label={t(
                                'worklist.addCustomTask.orderType.label',
                              )}
                            />
                          );
                        }}
                      />
                      {(error || (orderType && orderTypeCategory)) && (
                        <FormHelperText error={Boolean(error)}>
                          {error?.message ??
                            `${t('worklist.addCustomTask.orderCategory')}: ${orderTypeCategory}`}
                        </FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />
            )}
            {taskType === 'Care management' &&
              orderType === 'Completed task' && (
                <NumberInputFormField
                  name='expected_minutes'
                  label={t('worklist.addCustomTask.expected_minutes')}
                />
              )}
            {taskType === 'Care management' && orderType && (
              <Controller
                name='order_set'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        disablePortal
                        options={orderSetOptions}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        getOptionLabel={opt => opt.name}
                        value={
                          orderSetOptions.find(o => o.id === field.value) ??
                          null
                        }
                        onChange={(_, v) => field.onChange(v?.id)}
                        renderInput={params => (
                          <TextField
                            {...params}
                            required
                            label={t('worklist.addCustomTask.orderSet.label')}
                            error={!isNil(error)}
                          />
                        )}
                      />
                      <FormHelperText error={!isNil(error)}>
                        {error ? error.message : null}
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              />
            )}
            {taskType === 'Care management' && orderType === 'Labs' && (
              <Controller
                name='lab_requirements'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        disablePortal
                        options={labRequirementsOptions}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        getOptionLabel={opt => opt.name}
                        value={
                          labRequirementsOptions.find(
                            o => o.id === field.value,
                          ) ?? null
                        }
                        onChange={(_, v) => field.onChange(v?.id)}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'worklist.addCustomTask.labRequirements.label',
                            )}
                            error={!isNil(error)}
                            required
                          />
                        )}
                      />
                      <FormHelperText error={!isNil(error)}>
                        {error ? error.message : null}
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              />
            )}
            {taskType === 'Care management' && orderType === 'Home Health' && (
              <Controller
                name='home_health_agency_name'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        disablePortal
                        options={homeHealthAgencyNameOptions}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        getOptionLabel={opt => opt.name}
                        value={
                          homeHealthAgencyNameOptions.find(
                            o => o.id === field.value,
                          ) ?? null
                        }
                        onChange={(_, v) => field.onChange(v?.id)}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'worklist.addCustomTask.homeHealthAgencyName.label',
                            )}
                            error={!isNil(error)}
                            required
                          />
                        )}
                      />
                      <FormHelperText error={!isNil(error)}>
                        {error ? error.message : null}
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              />
            )}
            {taskType === 'Care management' && orderType === 'Home Health' && (
              <Controller
                name='home_health_provider_status'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        disablePortal
                        options={homeHealthProviderStatusOptions}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        getOptionLabel={opt => opt.name}
                        value={
                          homeHealthProviderStatusOptions.find(
                            o => o.id === field.value,
                          ) ?? null
                        }
                        onChange={(_, v) => field.onChange(v?.id)}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'worklist.addCustomTask.homeHealthProviderStatus.label',
                            )}
                            error={!isNil(error)}
                          />
                        )}
                      />
                      <FormHelperText error={!isNil(error)}>
                        {error ? error.message : null}
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              />
            )}
            {taskType === 'Care management' && orderType === 'Hospice' && (
              <Controller
                name='hospice_referral_origin'
                control={control}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormControl fullWidth>
                      <Autocomplete
                        disablePortal
                        options={hospiceReferralOriginOptions}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        getOptionLabel={opt => opt.name}
                        value={
                          hospiceReferralOriginOptions.find(
                            o => o.id === field.value,
                          ) ?? null
                        }
                        onChange={(_, v) => field.onChange(v?.id)}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t(
                              'worklist.addCustomTask.hospiceReferralOrigin.label',
                            )}
                            error={!isNil(error)}
                            required
                          />
                        )}
                      />
                      <FormHelperText error={!isNil(error)}>
                        {error ? error.message : null}
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              />
            )}
            {taskType === 'Care management' &&
              orderType === 'Hospice' &&
              orderSet === 'External / Other' && (
                <TextFormField
                  name='external_hospice_agency_name'
                  label={t('worklist.addCustomTask.externalHospiceAgencyName')}
                />
              )}
            {taskType === 'Care management' &&
              orderType === 'Hospice' &&
              orderSet === 'External / Other' && (
                <Controller
                  name='external_hospice_referral_reason'
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormControl fullWidth>
                        <Autocomplete
                          disablePortal
                          options={externalHospiceReferralReasonOptions}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          getOptionLabel={opt => opt.name}
                          value={
                            externalHospiceReferralReasonOptions.find(
                              o => o.id === field.value,
                            ) ?? null
                          }
                          onChange={(_, v) => field.onChange(v?.id)}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label={t(
                                'worklist.addCustomTask.externalHospiceReferralReason.label',
                              )}
                              error={!isNil(error)}
                              required
                            />
                          )}
                        />
                        <FormHelperText error={!isNil(error)}>
                          {error ? error.message : null}
                        </FormHelperText>
                      </FormControl>
                    );
                  }}
                />
              )}

            {taskType === 'Care management' &&
              orderSet === 'External Referral' && (
                <TextFormField
                  optional={false}
                  name='external_agency_name'
                  label={t('worklist.addCustomTask.externalAgencyName')}
                />
              )}

            {taskType === 'Care management' &&
              orderSet === 'External Referral' && (
                <SelectFormField
                  optional={false}
                  name='reason_for_external_referral'
                  label={t('worklist.addCustomTask.reasonForExternalReferral')}
                  options={reasonForExternalReferralOptions}
                />
              )}

            {taskType === 'Care management' &&
              (orderSet === 'Other' ||
                (homeHealthAgencyName === 'Other' &&
                  orderType === 'Home Health') ||
                (orderType === 'Hospice' &&
                  externalHospiceAgencyName === 'Other') ||
                (orderType === 'Hospice' &&
                  hospiceReferralOrigin === 'Other') ||
                (orderType === 'Hospice' &&
                  orderSet === 'External / Other' &&
                  externalHospiceReferralReason === 'Other') ||
                (orderSet === 'External Referral' &&
                  reasonForExternalReferral === 'Other')) && (
                <Controller
                  name='other_specifier'
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      sx={{ width: '100%', mb: '0.5rem' }}
                      type='text'
                      label={t('worklist.addCustomTask.otherSpecifier')}
                      variant='outlined'
                      fullWidth
                      error={!isNil(error)}
                      required
                      helperText={
                        error
                          ? error.message
                          : `${field.value ? field.value.length + `/${MAXIMUM_STRING_LENGTH}` : ''}`
                      }
                      slotProps={{
                        formHelperText: {
                          style: {
                            textAlign: error ? 'left' : 'right',
                            margin: 0,
                            padding: '3px 14px 0',
                          },
                        },
                      }}
                    />
                  )}
                />
              )}

            {(taskType === 'Custom' || taskType === 'Care management') && (
              <SelectFormField
                name='recurrence'
                label={t('worklist.recurrence.label')}
                options={recurrenceOptions}
                {...(selectedRecurrence && {
                  helperText:
                    selectedRecurrence === '-1'
                      ? t('worklist.recurrence.infoOneTime')
                      : t('worklist.recurrence.infoRecurrent', {
                          days: selectedRecurrence,
                        }),
                })}
              />
            )}

            {taskType === 'Care management' &&
              orderType === 'Completed task' && (
                <Box
                  sx={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}
                >
                  <Typography variant='subtitle2' sx={{ pb: 1, width: '100%' }}>
                    Care Notes
                  </Typography>

                  <Controller
                    name='note_program_id'
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Select
                        {...field}
                        value={field.value || ''}
                        type='text'
                        size='medium'
                        label='Program (optional)'
                        variant='outlined'
                        fullWidth
                        error={!isNil(error)}
                        helperText={error ? error.message : ''}
                        options={programOptions}
                        clearable
                        clearLabel='None'
                      />
                    )}
                  />

                  <Controller
                    name='note_type'
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Select
                        {...field}
                        value={field.value || ''}
                        type='text'
                        size='medium'
                        label='Note type'
                        variant='outlined'
                        fullWidth
                        required
                        error={!isNil(error)}
                        helperText={error ? error.message : ''}
                        options={noteTypeOptions}
                      />
                    )}
                  />

                  <Controller
                    name='note'
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        sx={{ width: '100%', mb: '0.5rem' }}
                        type='text'
                        label='Notes'
                        variant='outlined'
                        fullWidth
                        minRows={4}
                        maxRows={6}
                        multiline
                        error={!isNil(error)}
                        helperText={
                          error
                            ? error.message
                            : `${field.value ? field.value.length + `/${MAXIMUM_TEXT_AREA_LENGTH}` : ''}`
                        }
                        slotProps={{
                          formHelperText: {
                            style: {
                              textAlign: error ? 'left' : 'right',
                              margin: 0,
                              padding: '3px 14px 0',
                            },
                          },
                        }}
                        required={true}
                      />
                    )}
                  />

                  <Controller
                    name='notable_call'
                    control={control}
                    render={({ field }) => (
                      <Box sx={{ display: 'flex', width: '100%' }}>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                onChange={e => {
                                  field.onChange(e.target.checked);
                                }}
                                checked={!!field.value}
                                value={field.value}
                              />
                            }
                            label='Notable'
                          />
                        </FormGroup>
                      </Box>
                    )}
                  />
                </Box>
              )}

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting || !isValid}
              >
                {t('general.ADD')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isLoading}
      />
    </>
  );
};
